import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'

import { useCalculationStore } from '@/stores/calculation'
import { useEnvironmentDatabaseStore } from '@/stores/environmentDatabase'
import { useProductStore } from '@/stores/product'
import { useConfirmDeleteStore } from '@/stores/confirmDelete'
import { useCalculationEnvironmentDatabaseSelectionStore } from '@/stores/calculationEnvironmentDatabaseSelection'
import { useCalculationDatabaseSwitch } from '@/components/calculation-form/composable/useCalculationDatabaseSwitch'
import type { CalculationFormMixinProps } from '@/components/calculation-form/type/type'
import { extractDatabaseNameFromFileName } from '@/views/building_blocks/utils/buildingBlockViewUtils'
import type {
  EnvironmentDatabase,
  EnvironmentDatabaseUsage
} from '@/stores/type/environmentDatabase.type'

type DatabaseRowStatus = {
  label: string
  color: 'primary' | 'success' | 'grey'
  icon?: string
  tooltip?: string
}

type DatabaseRowViewModel = {
  database: EnvironmentDatabase
  status: DatabaseRowStatus
}

export function useEnvironmentDatabaseTab(props: CalculationFormMixinProps) {
  const route = useRoute()

  const calculationStore = useCalculationStore()
  const { currentItem } = storeToRefs(calculationStore)
  const isReadOnly = computed(() => !calculationStore.canChangeCalculation || props.readonly)

  const environmentDatabaseStore = useEnvironmentDatabaseStore()
  const productStore = useProductStore()
  const confirmDeleteStore = useConfirmDeleteStore()
  const environmentDatabaseSelectionStore = useCalculationEnvironmentDatabaseSelectionStore()
  const { items: databases } = storeToRefs(environmentDatabaseStore)

  const fileInput = ref<HTMLInputElement | null>(null)
  const importLoadingVisible = ref(false)
  const importLoadingMessage = ref('')

  const showDeleteBlockedDialog = ref(false)
  const deleteBlockedDatabaseName = ref('')
  const deleteBlockedCalculationNames = ref<string[]>([])
  const deleteBlockedDialogTitle = ref('Environmental database in use')
  const deleteBlockedDialogDescription = ref(
    'This environmental database cannot be deleted because it is still selected or used by one or more calculations.\n\nIf you want to remove this database, delete the calculation that still uses it first.'
  )

  const projectId = computed(() => {
    const routeProjectId = Number(route.params.id || 0)
    if (routeProjectId > 0) return routeProjectId
    return Number(currentItem.value?.project || 0)
  })

  const calculationId = computed(() => {
    return Number(route.params.calculationId || currentItem.value?.id || 0)
  })

  const {
    selectedDatabase,
    onDatabaseChange,
    operationLoadingVisible,
    operationLoadingMessage,
    showSwitchValidationSummaryDialog,
    affectedBuildingBlocks,
    switchBaseDatabaseName,
    switchTargetDatabaseName
  } = useCalculationDatabaseSwitch({
    isReadOnly,
    projectId,
    calculationId
  })

  const databaseOptions = computed(() =>
    databases.value.map((database) => ({ label: database.name, value: database.id }))
  )

  const getExcludeCalculationId = () => {
    return calculationId.value > 0 ? calculationId.value : undefined
  }

  const fetchDatabases = async () => {
    if (!projectId.value) return
    const params: Record<string, number> = { project: projectId.value }
    const excludeCalculationId = getExcludeCalculationId()
    if (excludeCalculationId) {
      params.exclude_calculation = excludeCalculationId
    }
    await environmentDatabaseStore.fetchItems(params)
  }

  const canDelete = (database: EnvironmentDatabase) => {
    if (isReadOnly.value) return false
    return !!database.can_delete
  }

  const openImportLoading = (message: string) => {
    importLoadingMessage.value = message
    importLoadingVisible.value = true
  }

  const closeImportLoading = () => {
    importLoadingVisible.value = false
    importLoadingMessage.value = ''
  }

  const handleImportEnvDb = () => {
    if (isReadOnly.value) return
    if (fileInput.value) fileInput.value.value = ''
    fileInput.value?.click()
  }

  const onEnvDbFileSelected = async (event: Event) => {
    if (isReadOnly.value) return
    if (!(event.target instanceof HTMLInputElement)) return

    const target = event.target
    const file = target.files?.[0]
    if (!file) return
    if (!projectId.value) return

    const formData = new FormData()
    formData.append('file', file)
    formData.append('project', projectId.value.toString())

    const databaseName = extractDatabaseNameFromFileName(file.name)
    openImportLoading(`Uploading ${databaseName}`)

    try {
      await productStore.updateFile(formData)
      await fetchDatabases()
    } catch (error) {
      environmentDatabaseStore.setSnackbarMessage(
        'Failed to import environmental database',
        'error'
      )
      console.error('Failed to import environmental database:', error)
    } finally {
      closeImportLoading()
    }
  }

  const clearStaleSelection = () => {
    const validIds = databases.value.map((db) => Number(db.id))
    environmentDatabaseSelectionStore.clearInvalidSelectedDatabase(calculationId.value, validIds)
    if (
      currentItem.value &&
      currentItem.value.database != null &&
      !validIds.includes(Number(currentItem.value.database))
    ) {
      currentItem.value = { ...currentItem.value, database: null }
    }
  }

  const getBlockingCalculationNames = (usage: EnvironmentDatabaseUsage): string[] => {
    const calculationEntries = [...usage.used_by_calculations, ...usage.selected_by_calculations]
    const seenCalculationKeys = new Set<string>()
    const calculationNames: string[] = []

    for (const calculation of calculationEntries) {
      const name = calculation.name?.trim()
      if (!name) continue

      const uniqueKey = calculation.id ? `id-${calculation.id}` : `name-${name}`
      if (seenCalculationKeys.has(uniqueKey)) continue

      seenCalculationKeys.add(uniqueKey)
      calculationNames.push(name)
    }

    return calculationNames
  }

  const onSelectedDatabaseChange = async (newId: number | null) => {
    const switched = await onDatabaseChange(newId)
    if (!switched) return
    await fetchDatabases()
  }

  const resolveDatabaseStatus = (database: EnvironmentDatabase): DatabaseRowStatus => {
    const selectedDatabaseId = Number(selectedDatabase.value)
    if (selectedDatabase.value != null && Number(database.id) === selectedDatabaseId) {
      return {
        label: 'Selected',
        color: 'primary',
        icon: 'mdi-star-circle',
        tooltip: 'Selected for this calculation'
      }
    }

    if (database.in_use) {
      return {
        label: 'In use',
        color: 'success',
        icon: 'mdi-check-circle',
        tooltip: 'In use by a calculation'
      }
    }

    return {
      label: 'Not in use',
      color: 'grey'
    }
  }

  const databaseRows = computed<DatabaseRowViewModel[]>(() => {
    return databases.value.map((database) => ({
      database,
      status: resolveDatabaseStatus(database)
    }))
  })

  const onDeleteDatabase = async (database: EnvironmentDatabase) => {
    if (!canDelete(database)) return

    const confirmed = await confirmDeleteStore.confirm({
      title: 'Delete environmental database',
      message: 'Delete this environmental database? This action cannot be undone.',
      name: database.name,
      confirmText: 'Delete',
      cancelText: 'Cancel'
    })

    if (!confirmed) return

    const excludeCalculationId = getExcludeCalculationId()

    try {
      await environmentDatabaseStore.deleteWithContext(database, excludeCalculationId)
      await fetchDatabases()
      clearStaleSelection()
    } catch (error) {
      const errorObj = error as Record<string, unknown>
      const statusCode = errorObj?.status as number | undefined

      if (statusCode === 409) {
        const usage = await environmentDatabaseStore.fetchUsage(database.id, excludeCalculationId)
        if (usage && !usage.can_delete) {
          deleteBlockedDatabaseName.value = database.name
          deleteBlockedCalculationNames.value = getBlockingCalculationNames(usage)
          showDeleteBlockedDialog.value = true
        }
      } else {
        environmentDatabaseStore.setSnackbarMessage(
          'Failed to delete environmental database',
          'error'
        )
      }
      console.error('Failed to delete environmental database:', error)
    }
  }

  onMounted(async () => {
    await fetchDatabases()
    props.updateTabCompletion(props.tab, true)
  })

  return {
    databases,
    databaseRows,
    databaseOptions,
    selectedDatabase,
    onSelectedDatabaseChange,
    operationLoadingVisible,
    operationLoadingMessage,
    showSwitchValidationSummaryDialog,
    affectedBuildingBlocks,
    switchBaseDatabaseName,
    switchTargetDatabaseName,
    fileInput,
    importLoadingVisible,
    importLoadingMessage,
    handleImportEnvDb,
    onEnvDbFileSelected,
    canDelete,
    onDeleteDatabase,
    showDeleteBlockedDialog,
    deleteBlockedDatabaseName,
    deleteBlockedCalculationNames,
    deleteBlockedDialogTitle,
    deleteBlockedDialogDescription
  }
}
